import React, { useEffect, useRef, useState } from 'react';

const ONLINE_DISPLAY_MS = 3000;

type NetworkStatus = 'hidden' | 'offline' | 'online';

const getMessage = (status: NetworkStatus) => {
    if (status === 'offline') return 'No internet connection';
    if (status === 'online') return 'Back online';
    return null;
};

const NetworkStatusBar = () => {
    const [status, setStatus] = useState<NetworkStatus>('hidden');
    const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const wasOffline = useRef(false);

    useEffect(() => {
        const clearHideTimeout = () => {
            if (hideTimeout.current) {
                clearTimeout(hideTimeout.current);
                hideTimeout.current = null;
            }
        };

        const showOffline = () => {
            clearHideTimeout();
            wasOffline.current = true;
            setStatus('offline');
        };

        const showOnline = () => {
            clearHideTimeout();

            if (!wasOffline.current) {
                setStatus('hidden');
                return;
            }

            setStatus('online');
            wasOffline.current = false;
            hideTimeout.current = setTimeout(() => {
                setStatus('hidden');
            }, ONLINE_DISPLAY_MS);
        };

        if (!window.navigator.onLine) {
            showOffline();
        }

        window.addEventListener('offline', showOffline);
        window.addEventListener('online', showOnline);

        return () => {
            clearHideTimeout();
            window.removeEventListener('offline', showOffline);
            window.removeEventListener('online', showOnline);
        };
    }, []);

    const message = getMessage(status);

    return (
        <div
            aria-live="polite"
            className={`network-status ${status}`}
            role={message ? 'status' : undefined}
        >
            {message}

            <style jsx>{`
                .network-status {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    z-index: 10001;
                    box-sizing: border-box;
                    min-height: calc(36px + env(safe-area-inset-bottom));
                    padding: 8px 16px calc(8px + env(safe-area-inset-bottom));
                    color: #fff;
                    font-size: 14px;
                    font-weight: 700;
                    line-height: 20px;
                    text-align: center;
                    transform: translateY(0);
                    transition: bottom 0.25s cubic-bezier(0.4, 0, 0.2, 1),
                        transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                    will-change: transform;
                    pointer-events: none;
                    box-shadow: 0 1px 3px rgba(38, 50, 56, 0.2);
                }

                .network-status.offline {
                    background: #f44034;
                }

                .network-status.online {
                    background: #43a047;
                }

                .network-status.hidden {
                    transform: translateY(100%);
                }

            `}</style>
        </div>
    );
};

export default NetworkStatusBar;
